import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Shield, Eye, EyeOff, Check, ArrowLeft } from "lucide-react";

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score; // 0-5
}

const STRENGTH_LABELS = ["Très faible", "Faible", "Moyen", "Bon", "Fort", "Excellent"];
const STRENGTH_COLORS = ["bg-red-500", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500", "bg-emerald-500"];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  // Un mot de passe fort est conseillé (voir la jauge ci-dessous) mais non obligatoire.
  const canSubmitPassword = password.length > 0;

  const handleResetPassword = async () => {
    if (!token) {
      toast.error("Lien invalide, veuillez refaire une demande");
      navigate("/forgot-password", { replace: true });
      return;
    }
    if (!canSubmitPassword) {
      toast.error("Veuillez entrer un mot de passe");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/confirm`, { token, password });
      toast.success("Mot de passe changé avec succès");
      navigate("/password-login", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 top-[18%] h-[380px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 via-indigo-600/15 to-violet-600/20 blur-[100px]"></div>
        <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,#000_20%,transparent_70%)]"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <a href="/" className="mb-7 flex items-center justify-center gap-2.5">
          <img src="/images/logo-navbar.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-white/10" />
          <span className="font-heading text-lg font-bold tracking-tight text-white">VantaPulse</span>
        </a>
        <div className="bg-[#0f1019]/90 backdrop-blur-2xl border border-white/[0.09] rounded-[26px] p-8 sm:p-9 shadow-[0_40px_120px_-30px_rgba(59,130,246,0.45)] relative overflow-hidden animate-slideUp">
          <div className="text-center mb-8 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 to-violet-500/15 blur-3xl rounded-full"></div>
            <div className="relative">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-blue-900/40 ring-1 ring-white/10">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-heading text-[26px] font-bold tracking-tight text-white mb-2">
                Changez votre mot de passe
              </h2>
              <p className="text-white/50 text-sm">Choisissez un nouveau mot de passe sécurisé</p>
            </div>
          </div>

          {!token && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm text-center">
              Lien invalide ou expiré. Refaites une demande de réinitialisation.
            </div>
          )}

          <div className="space-y-5 mb-8">
            <div className="group">
              <label className="block text-[13px] text-white/75 mb-1.5 font-medium">Nouveau mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="relative w-full px-4 py-3.5 pr-12 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {password.length > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-full flex-1 rounded-full transition-colors ${i < strength ? STRENGTH_COLORS[strength] : "bg-transparent"}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-white/40 mt-1">{STRENGTH_LABELS[strength]} — conseil : min. 8 caractères, majuscule, minuscule et chiffre</p>
                </div>
              )}
            </div>

            <div className="group">
              <label className="block text-[13px] text-white/75 mb-1.5 font-medium">Répétez le mot de passe</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="relative w-full px-4 py-3.5 pr-12 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => navigate("/forgot-password")}
              className="flex-1 py-3.5 bg-white/[0.04] hover:bg-white/[0.08] text-white/85 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 border border-white/[0.08]"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour
            </button>
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="flex-1 py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:brightness-110 active:scale-[0.99] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)]"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Changement...
                </>
              ) : (
                <>
                  Changer
                  <Check className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .animate-slideUp {
            animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        `}</style>
      </div>
    </div>
  );
}
